/**
 * RfidScannerContext.
 * (2026-04-24, Jason Cox) App-level singleton for the R6 UHF hand scanner.
 *
 * Before this, every page that mounted RfidScannerPanel mounted a fresh
 * useRfidScanner hook, which created its own RfidScanner and disconnected
 * BLE in its cleanup -- so switching pages dropped the connection every
 * time. This provider owns one RfidScanner for the authenticated app's
 * lifetime; the connection now persists across route changes and only
 * closes when the user clicks Disconnect (or the device goes out of range).
 *
 * Tag-scan delivery is fan-out: consumers register a listener via
 * subscribeTagScanned; every scanned EPC is dispatched to all registered
 * listeners. In practice only the currently-mounted page registers one,
 * so the active page receives scans while the others do nothing.
 */
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { BleDeviceInfo, RfidScanner } from '../utils/rfidScanner';

type TagListener = (epc: string) => void;
type ErrorListener = (message: string) => void;

export interface RfidScannerContextValue {
  scanner: RfidScanner | null;
  connected: boolean;
  connecting: boolean;
  deviceInfo: BleDeviceInfo | null;
  statusText: string;
  batteryLevel: number;
  scanPower: number;
  setScanPower: (power: number) => void;
  connect: () => Promise<void>;
  disconnect: () => void;
  trigger: () => Promise<void>;
  /** Register a tag-scanned listener. Returns an unsubscribe fn. */
  subscribeTagScanned: (listener: TagListener) => () => void;
  /** Register an error listener. Returns an unsubscribe fn. */
  subscribeError: (listener: ErrorListener) => () => void;
}

const RfidScannerContext = createContext<RfidScannerContextValue | undefined>(undefined);

const DEFAULT_POWER = 24;

export const RfidScannerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const scannerRef = useRef<RfidScanner | null>(null);
  const tagListenersRef = useRef<Set<TagListener>>(new Set());
  const errorListenersRef = useRef<Set<ErrorListener>>(new Set());

  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<BleDeviceInfo | null>(null);
  const [statusText, setStatusText] = useState('Not connected');
  const [batteryLevel, setBatteryLevel] = useState<number>(-1);
  const [scanPower, setScanPowerState] = useState<number>(DEFAULT_POWER);

  // Lazy-create the singleton scanner once for the app's lifetime. No
  // cleanup function -- we intentionally keep the BLE connection alive
  // across page transitions until the user explicitly disconnects.
  useEffect(() => {
    if (scannerRef.current) return;
    const scanner = new RfidScanner();
    scanner.onTagScanned = (epc) => {
      tagListenersRef.current.forEach((cb) => {
        try { cb(epc); } catch { /* isolate listener errors */ }
      });
    };
    scanner.onBleStatusChange = (isConnected, info) => {
      setConnected(isConnected);
      setDeviceInfo(isConnected ? info : null);
      setStatusText(isConnected ? `Connected: ${info.name}` : 'Not connected');
      if (!isConnected) setBatteryLevel(-1);
    };
    scanner.onBatteryLevel = (level) => {
      setBatteryLevel(Math.max(0, Math.min(100, level)));
    };
    scannerRef.current = scanner;
  }, []);

  // Post-connect init + battery polling. Re-runs when power changes so the
  // UHF module is re-initialized at the new power.
  useEffect(() => {
    if (!connected) return;
    const scanner = scannerRef.current;
    if (!scanner) return;

    const initTimer = setTimeout(() => {
      scanner.initUhfModule(scanPower).catch(() => {});
    }, 3000);
    const firstBatteryTimer = setTimeout(() => {
      scanner.getBattery().catch(() => {});
    }, 3000);
    const batteryPoll = setInterval(() => {
      scanner.getBattery().catch(() => {});
    }, 30000);

    return () => {
      clearTimeout(initTimer);
      clearTimeout(firstBatteryTimer);
      clearInterval(batteryPoll);
    };
  }, [connected, scanPower]);

  const emitError = useCallback((msg: string) => {
    errorListenersRef.current.forEach((cb) => {
      try { cb(msg); } catch { /* ignore */ }
    });
  }, []);

  const connect = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    if (!RfidScanner.isWebBluetoothAvailable) {
      const msg = 'Web Bluetooth not available. Use Chrome on Android or desktop.';
      setStatusText(msg);
      emitError(msg);
      return;
    }
    setConnecting(true);
    setStatusText('Scanning for nearby devices...');
    try {
      await scanner.discoverAndConnect();
    } catch (err: any) {
      if (err?.name !== 'NotFoundError') {
        emitError(`BLE: ${err.message}`);
      }
      setStatusText('No device selected');
    } finally {
      setConnecting(false);
    }
  }, [emitError]);

  const disconnect = useCallback(() => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    scanner.disconnectBle();
    setConnected(false);
    setDeviceInfo(null);
    setStatusText('Not connected');
    setBatteryLevel(-1);
  }, []);

  const trigger = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      await scanner.triggerScan();
    } catch (err: any) {
      emitError(`Scan failed: ${err.message}`);
    }
  }, [emitError]);

  const setScanPower = useCallback((power: number) => {
    setScanPowerState(power);
  }, []);

  const subscribeTagScanned = useCallback((listener: TagListener) => {
    tagListenersRef.current.add(listener);
    return () => { tagListenersRef.current.delete(listener); };
  }, []);

  const subscribeError = useCallback((listener: ErrorListener) => {
    errorListenersRef.current.add(listener);
    return () => { errorListenersRef.current.delete(listener); };
  }, []);

  const value: RfidScannerContextValue = {
    scanner: scannerRef.current,
    connected,
    connecting,
    deviceInfo,
    statusText,
    batteryLevel,
    scanPower,
    setScanPower,
    connect,
    disconnect,
    trigger,
    subscribeTagScanned,
    subscribeError,
  };

  return (
    <RfidScannerContext.Provider value={value}>
      {children}
    </RfidScannerContext.Provider>
  );
};

export const useRfidScannerContext = (): RfidScannerContextValue => {
  const ctx = useContext(RfidScannerContext);
  if (!ctx) {
    throw new Error('useRfidScannerContext must be used within RfidScannerProvider');
  }
  return ctx;
};
